import { Model, modelRegistry } from './Model';
import { OrderTicketItem } from './OrderTicketItem';
import { Order, Product, KitchenTicket, Pricelist, Client, Terminal, User } from '../models';
import { OrderTicketStatus, OrderFlagType, OrderStatusType, PaymentTypeLabel, PrintRoleType } from '../types';
import { Money, dateFormatter } from '../utilities';
import { apiService, connectionService, getProviderLabel, OrderService } from '../services';
import { printService } from '../print/PrintService';
import { Store, I18n } from '../wrappers';
import { HashGen } from '../utilities';
import { Printable } from '../print/Printable';
import moment from 'moment-timezone';
/**
 * Statuses from which a delivery order may still be fiscalized. The kitchen
 * must be done (so the contents are final), but anything after that is fair
 * game because the platform's own courier events can outrun the cashier.
 */
const ISSUABLE_STATUSES = [
    OrderTicketStatus.ready,
    OrderTicketStatus.completed,
    OrderTicketStatus.pickedUp,
];
export class OrderTicket extends Model {
    static entity = 'order_tickets';
    static primaryKey = 'hash';
    static syncable = true;
    id = null;
    hash = '';
    external_id = null;
    external_code = null;
    source = '';
    venue_id = null;
    status = OrderTicketStatus.accepted;
    order_id = null;
    order_hash = null;
    payment_method = null;
    callout_code = null;
    special_requirements = null;
    estimated_pickup_time = null;
    expedition_type = null;
    min_pickup_at = null;
    max_pickup_at = null;
    rider_pickup_time = null;
    /** DH certification/test order — TEST badge on the POS, never auto-fired to the kitchen. */
    is_test = false;
    prepared_at = null;
    created_at = '';
    updated_at = '';
    items = [];
    /** Client-only flag: true when auto mark-ready from KDS failed (not persisted to backend) */
    markReadyFailed = false;
    /** Tracks order tickets currently being completed — prevents double-issue across store recomputations */
    static _completingHashes = new Set();
    static fields() {
        return {
            id: this.attr(null),
            hash: this.attr(HashGen.long()),
            external_id: this.attr(null),
            external_code: this.attr(null),
            source: this.attr(''),
            venue_id: this.attr(null),
            status: this.attr(OrderTicketStatus.accepted),
            order_id: this.attr(null),
            order_hash: this.attr(null),
            payment_method: this.attr(null),
            callout_code: this.attr(null),
            special_requirements: this.attr(null),
            estimated_pickup_time: this.attr(null),
            expedition_type: this.attr(null),
            min_pickup_at: this.attr(null),
            max_pickup_at: this.attr(null),
            rider_pickup_time: this.attr(null),
            is_test: this.attr(false),
            prepared_at: this.attr(null),
            created_at: this.attr(''),
            updated_at: this.attr(''),
            items: this.attr([], (value) => (value || []).map(item => new OrderTicketItem(item))),
            markReadyFailed: this.attr(false),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    // ── Static methods ──
    static async onKitchenTicketDone(orderTicketHash) {
        const orderTicket = OrderTicket.all().find(t => t.hash === orderTicketHash);
        if (orderTicket && orderTicket.isPreparing()) {
            const result = await orderTicket.markReady();
            if (!result.success) {
                console.error('[OrderTicket] Auto mark-ready failed for ticket', orderTicket.hash);
                orderTicket.markReadyFailed = true;
                orderTicket.$save();
                // Use callout_code (the unified delivery identifier) so the kitchen
                // always sees the same code that appears on the slip and receipt.
                const code = orderTicket.callout_code || '';
                return { failed: true, externalCode: code };
            }
        }
        return null;
    }
    /**
     * Can this terminal fiscally process orders? True for cashRegister and
     * orderingDevice terminal types; false for kitchenDisplaySystem. The
     * downstream OrderValidation.validateGeneral still enforces that a
     * CashRegister is associated and a Shift is open before fiscalization
     * actually proceeds — this predicate just gates terminal *types* that
     * are allowed to attempt it.
     */
    static canProcess() {
        const terminal = Store.get('terminal');
        return !!terminal && (terminal.isCashRegister() || terminal.isOrderingDevice());
    }
    /**
     * Single-firer election for auto-fire. The `orderTicket`
     * creation broadcast reaches every terminal at once; without coordination
     * each fire-capable terminal independently runs fireToKitchen, producing
     * N KDS cards / kitchen slips / callout codes for one delivery order (the
     * local dedup guard misses because no peer has synced its kitchen ticket
     * yet at broadcast time).
     *
     * Deterministic auto-pick (no configuration): among fire-capable terminals
     * (cashRegister + orderingDevice) sorted by id, the lowest-id terminal is
     * rank 0 — the designated firer, which fires immediately. Higher ranks act
     * only as a self-healing fallback (see autoFireIfNeeded): they wait a
     * rank-scaled delay and re-check, so a single offline primary doesn't
     * strand delivery tickets unfired. This mirrors Toast's "one auto-fire
     * device" model; a venue-configured override (Toast-style auto-fire device
     * setting) can replace the rank rule later.
     *
     * @returns the current terminal's rank, or null if it is not fire-capable.
     */
    static autoFireRank() {
        const terminal = Store.get('terminal');
        if (!terminal || !(terminal.isCashRegister() || terminal.isOrderingDevice()))
            return null;
        const fireCapable = Terminal.all()
            .filter(t => t.isCashRegister() || t.isOrderingDevice())
            .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        const idx = fireCapable.findIndex(t => t.id === terminal.id);
        return idx < 0 ? null : idx;
    }
    /**
     * Merge a server-authoritative OrderTicket record into the local store
     * without clobbering POS-owned, monotonic state. A blind upsert by hash
     * (status broadcast or snapshot delta) can carry a record read BEFORE a
     * local completion PATCH committed — order_hash null, an already-cancelled
     * ticket reverted — and wholesale-replace the local record, wiping the
     * completion backfill (re-opening the Issue button on an already-issued
     * ticket) or resurrecting a locally-cancelled ticket. Guards:
     *   - never resurrect a locally-cancelled ticket (terminal locally),
     *   - preserve local order_hash/order_id/callout_code/prepared_at when the
     *     incoming record lacks them (POS-allocated, monotonic — never unset).
     *
     * Status itself is NOT guarded here: the fiscal failure modes (double
     * issue, double storno) are already blocked at the action layer
     * (completeAndIssue's order_id/_completingHashes checks; storno's
     * canceled() guard), matching how every other syncable entity relies on
     * action-layer idempotency rather than a merge-layer lifecycle ladder.
     */
    static mergeServerRecord(record) {
        const local = OrderTicket.find(record.hash);
        if (!local) {
            OrderTicket.insert({ data: [record] });
            return;
        }
        if (local.status === OrderTicketStatus.cancelled)
            return;
        const merged = { ...record };
        if (!merged.order_hash && local.order_hash)
            merged.order_hash = local.order_hash;
        if (!merged.order_id && local.order_id)
            merged.order_id = local.order_id;
        if (!merged.callout_code && local.callout_code)
            merged.callout_code = local.callout_code;
        if (!merged.prepared_at && local.prepared_at)
            merged.prepared_at = local.prepared_at;
        OrderTicket.insert({ data: [merged] });
    }
    static getSettings() {
        const tenant = Store.get('tenant');
        return tenant?.venue?.settings?.order_ticket;
    }
    // ── Status checks ──
    hasUnmappedItems() {
        return this.items.some(i => !i.isMapped());
    }
    isActive() {
        return [
            OrderTicketStatus.scheduled,
            OrderTicketStatus.confirmedPreorder,
            OrderTicketStatus.accepted,
            OrderTicketStatus.needsMapping,
            OrderTicketStatus.preparing,
            OrderTicketStatus.ready,
        ].includes(this.status);
    }
    /**
     * True when this ticket was entered manually on the POS
     * (no external platform assigned a reference code).
     *
     * Manual tickets carry external_code === null — that absence is
     * the canonical marker. Use this helper instead of checking the
     * field directly so call sites read in positive framing.
     */
    isManual() {
        return !this.external_code;
    }
    isPreparing() {
        return this.status === OrderTicketStatus.preparing;
    }
    /**
     * Does this ticket still need sending to the kitchen?
     *
     * `preparing` alone does NOT mean fired: a delivery platform's own status
     * update sets it too (Wolt sends PRODUCTION seconds after our accept), so a
     * ticket that arrived while no terminal was online reached `preparing`
     * without any kitchen ever hearing about it — staff saw "in preparation"
     * with no KDS card and no slip (live QA 2026-08-04).
     *
     * `callout_code` is the marker instead, because for platform tickets it is
     * allocated exactly at fireToKitchen. Manual tickets are excluded: their
     * callout is allocated at submitAsTicket BEFORE the fire, so its presence
     * proves nothing — and they never receive platform status updates, so they
     * cannot land in this state anyway.
     */
    needsKitchenFire() {
        // Platform test/certification orders must never reach the kitchen
        // (the POS shows a TEST badge; staff walk the accept/ready flow only).
        if (this.is_test)
            return false;
        if (this.isManual())
            return false;
        if (this.hasUnmappedItems())
            return false;
        if (this.status === OrderTicketStatus.accepted)
            return true;
        return this.status === OrderTicketStatus.preparing && !this.callout_code;
    }
    /**
     * Is a human still expected to release this ticket to the kitchen?
     *
     * Only meaningful with `require_staff_confirmation` on, where nothing
     * auto-fires and this release is the ONLY path into the kitchen — so the
     * gate cannot be a plain `accepted` check. The platform advances the status
     * by itself (Wolt sends PRODUCTION on its own accept), which used to make
     * the release action disappear from a ticket the kitchen had never seen,
     * leaving the order unfireable (live QA 2026-08-04, order 587).
     *
     * `needsMapping` counts because a ticket keeps that status until it fires,
     * even after every line has been mapped by hand.
     *
     * A MANUAL ticket in `accepted` is releasable regardless of the setting:
     * that state only exists when its submit-time fire failed, and this release
     * is its designed retry — auto-fire skips manual tickets, so without it the
     * ticket would sit in Needs action with Reject as the only exit.
     */
    needsStaffKitchenRelease() {
        // Test orders never go to the kitchen — no release either.
        if (this.is_test)
            return false;
        if (this.hasUnmappedItems())
            return false;
        if (this.isManual())
            return this.status === OrderTicketStatus.accepted;
        if (!OrderTicket.getSettings()?.require_staff_confirmation)
            return false;
        // needsKitchenFire covers accepted and platform-advanced-but-unfired;
        // needs_mapping is the one extra state (kept until fire even after
        // every line has been mapped by hand).
        return this.status === OrderTicketStatus.needsMapping
            || this.needsKitchenFire();
    }
    isReady() {
        return this.status === OrderTicketStatus.ready;
    }
    /**
     * Instant the kitchen finished preparing — used to FREEZE the orders-hub
     * prep timer once the ticket reaches 'ready' (and beyond), so the timer
     * reflects prep time, not the courier's pickup wait. Returns null while the
     * ticket is still being prepared (timer keeps running).
     *
     * Uses the persisted prepared_at — stamped once at the ready transition and
     * never overwritten — so the value stays pure prep time across every lane,
     * including completed/history. Legacy tickets created before prepared_at
     * existed fall back to updated_at once prep is done (the old interim
     * behaviour: correct in the ready lane, approximate afterward).
     */
    prepEndedAt() {
        if (this.prepared_at)
            return this.prepared_at;
        const prepDone = [
            OrderTicketStatus.ready,
            OrderTicketStatus.pickedUp,
            OrderTicketStatus.completed,
            OrderTicketStatus.cancelled,
        ].includes(this.status);
        return prepDone ? (this.updated_at || null) : null;
    }
    isScheduled() {
        return this.status === OrderTicketStatus.scheduled;
    }
    // ── Sync ──
    async sync(action = 'update') {
        const apiAction = action === 'delete' ? 'delete' : (!this.id ? 'post' : 'patch');
        const url = '/order-tickets' + (this.id ? `/${this.id}` : '');
        let record = this.$toJson();
        await this.markForSync(action);
        if (action === 'delete')
            this.$delete();
        else
            this.$save();
        if (!Store.get('isOnline')) {
            OrderTicket.syncTerminals({ items: [{ action: apiAction, record }] });
            return { success: true };
        }
        try {
            const response = await apiService[apiAction](url, record);
            if (response.data.record) {
                record = response.data.record;
                // Preserve locally-set order references if backend response doesn't include them
                if (this.order_hash && !record.order_hash) {
                    record.order_hash = this.order_hash;
                    record.order_id = this.order_id;
                }
                // Preserve the locally-stamped prep-end time (monotonic, never unset)
                if (this.prepared_at && !record.prepared_at) {
                    record.prepared_at = this.prepared_at;
                }
                this.$fill(record);
                OrderTicket.insert({ data: [record] });
            }
            OrderTicket.syncTerminals({ items: [{ action: apiAction, record }] });
            await this.removeFromSync();
            return { success: true };
        }
        catch (error) {
            const isOnline = await connectionService.connectionCheckRetry(5000);
            return { success: isOnline };
        }
    }
    // ── Business logic ──
    /**
     * Fire the order ticket to the kitchen: create KDS ticket + print kitchen slips.
     * Does NOT create an Order. Can be called from any terminal type.
     */
    async fireToKitchen() {
        // `preparing` + a callout means someone really did fire this (us, a
        // peer, or submitAsTicket for a manual ticket). `preparing` with NO
        // callout means the platform moved the status and the kitchen was never
        // told — that one must still fire. See needsKitchenFire().
        if (this.status === OrderTicketStatus.preparing && (this.callout_code || this.isManual())) {
            return { success: true }; // already fired
        }
        if (this.hasUnmappedItems())
            return { success: false };
        // Allocate callout once. For manual hub tickets this was already
        // set at submitAsTicket; for API tickets we allocate here so the
        // printed slip carries the value. Backend enforces idempotency:
        // first PATCH to set a non-null callout wins (ADR-FN-009).
        if (!this.callout_code) {
            this.callout_code = OrderService.computeCalloutCode();
        }
        // Dedup guard: never create a second KDS card for one order ticket,
        // no matter which path re-enters fireToKitchen (stale event replay,
        // manual "Send to Kitchen" retry after a partial failure, status
        // regression from any source). printSlips stays outside the guard —
        // a legitimate retry must still print.
        const existingKitchenTicket = KitchenTicket.all().find(kt => kt.order_ticket_hash === this.hash);
        if (!existingKitchenTicket) {
            await KitchenTicket.issueFromOrderTicket(this);
        }
        this.printSlips();
        this.status = OrderTicketStatus.preparing;
        await this.sync();
        return { success: true };
    }
    /**
     * Complete and issue the order: create POS Order, fiscalize, print packing slip.
     * Called when courier arrives. Requires a fiscally-capable terminal type
     * (cashRegister or orderingDevice — see canProcess). The downstream
     * Order.issue / OrderValidation still verifies that a CashRegister is
     * actually associated and a Shift is open before fiscalizing.
     */
    async completeAndIssue() {
        if (!OrderTicket.canProcess()) {
            return { success: false, error: 'not_cash_register' };
        }
        // Issuable from `ready` onwards, not `ready` alone: the platform can
        // drive the ticket to completed/picked_up before staff get to the
        // receipt (a courier delivering seconds after the kitchen finishes),
        // and a ready-only gate left those sales permanently unfiscalizable —
        // goods delivered, no receipt, no way to make one. The order_id guard
        // below is what prevents a second receipt.
        if (!ISSUABLE_STATUSES.includes(this.status)) {
            return { success: false, error: 'not_ready' };
        }
        // order_hash FIRST: an order issued offline (or past the 2s sync
        // deadline) comes back with a null id, so order_id alone would let a
        // reload issue a SECOND fiscal receipt for the same ticket. The hash is
        // stamped locally at createNew, which is why the rest of this file and
        // the UI already treat it as the durable "already issued" marker.
        if (this.order_hash !== null || this.order_id !== null || OrderTicket._completingHashes.has(this.hash)) {
            return { success: false, error: 'already_processed' };
        }
        OrderTicket._completingHashes.add(this.hash);
        try {
            // ── Pre-validate: ensure all order items can be mapped ──
            let mappableCount = 0;
            for (const orderItem of this.items) {
                if (orderItem.getMappedProduct())
                    mappableCount++;
            }
            if (mappableCount === 0) {
                console.error('[OrderTicket] No valid items after mapping');
                OrderTicket._completingHashes.delete(this.hash);
                return { success: false, error: 'no_valid_items' };
            }
            if (mappableCount < this.items.length) {
                console.error(`[OrderTicket] ${this.items.length - mappableCount}/${this.items.length} items unmapped`);
                OrderTicket._completingHashes.delete(this.hash);
                return { success: false, error: 'items_skipped' };
            }
            // ── All items validated — safe to create Order ──
            const order = await Order.createNew();
            order.source = this.source;
            order.note = this.special_requirements || null;
            // Mirror the ticket's callout onto the Order so all render sites
            // (receipt, packing slip, kitchen slip) see a single source of truth.
            // The callout was allocated once — at submitAsTicket for manual hub
            // tickets, at fireToKitchen for API tickets. We don't reallocate here.
            if (this.callout_code) {
                order.setFlag(OrderFlagType.calloutCode, this.callout_code);
            }
            // payment_method only on API tickets; manual entries have no
            // meaningful value here and would print an empty placeholder.
            if (!this.isManual() && this.payment_method) {
                order.setFlag(OrderFlagType.deliveryPayment, this.payment_method);
            }
            // Platform's own order reference — the code the COURIER sees in
            // their app. Snapshotted so the packing slip can print it big for
            // bag matching; the callout stays the internal staff identifier.
            if (!this.isManual() && this.external_code) {
                order.setFlag(OrderFlagType.externalCode, this.external_code);
            }
            // Tag the order with the platform's client and pricing tier
            // when matching rows exist. Both are soft — proceed without
            // either if missing.
            const client = Client.findByPlatformSource(this.source);
            if (client) {
                order.client_id = client.id;
            }
            // Only assign a pricelist when the pricelists module is enabled.
            const pricelist = Pricelist.isModuleEnabled() ? Pricelist.findByPlatformSource(this.source) : null;
            if (pricelist) {
                order.setFlag(OrderFlagType.pricelist, String(pricelist.id));
            }
            for (const orderItem of this.items) {
                const product = orderItem.getMappedProduct();
                // Restore the snapshotted discount: unit_price is the
                // pre-discount price, discount_percent carries the platform
                // client discount, so getUnitPriceAfterDiscount (what the fiscal
                // device receives) matches what the cashier saw at submit.
                const discountPercent = Number(orderItem.discount_percent) || 0;
                const posItem = order.addItem(product, orderItem.quantity, discountPercent);
                if (posItem) {
                    posItem.unit_price = Money.parse(orderItem.unit_price).toFormat('0.0000');
                    if (orderItem.attributes.length) {
                        posItem.comments = [...orderItem.attributes];
                    }
                }
            }
            const total = order.getToPay();
            order.close({
                payments: [{ key: PaymentTypeLabel.other_noncash, amount: total.toUnit() }],
                status: OrderStatusType.paid,
            });
            const result = await order.issue({ skipPrintReceipt: false, skipPrintSlips: true });
            if (result.status === 'failed') {
                console.error('[OrderTicket] POS order issue failed', result.error);
                OrderTicket._completingHashes.delete(this.hash);
                return { success: false, error: 'issue_failed' };
            }
            // Print packing slip
            const tenant = Store.get('tenant');
            if (tenant?.venue?.settings?.order_ticket?.packing_slip_enabled !== false) {
                const receiptPrinter = printService.getAssignedPrinter(PrintRoleType.receipt);
                if (receiptPrinter) {
                    const packingPrintable = await order.getPackingSlipPrintable();
                    printService.print(packingPrintable, { printer: receiptPrinter });
                }
            }
            // Backfill references and save to store immediately.
            // issue() works on an internal $duplicate() — the server id is
            // $fill'ed into that duplicate, NOT into our `order` instance — so
            // read the id off the returned object. (`order.id` here is always
            // null.) order_hash is generated locally at createNew, so it is
            // valid on either object; it is the durable join key when the order
            // was issued offline and result.order.id is still 0.
            const issued = result.order ?? order;
            this.order_id = issued.id;
            this.order_hash = issued.hash;
            this.$save();
            await this.sync();
            // Backfill KitchenTicket
            const kitchenTicket = KitchenTicket.all().find(kt => kt.order_ticket_hash === this.hash);
            if (kitchenTicket) {
                kitchenTicket.order_id = issued.id;
                kitchenTicket.order_hash = issued.hash;
                kitchenTicket.sync('update');
            }
            OrderTicket._completingHashes.delete(this.hash);
            return { success: true };
        }
        catch (e) {
            console.error('[OrderTicket] Unexpected error during completeAndIssue', e);
            OrderTicket._completingHashes.delete(this.hash);
            return { success: false, error: 'issue_failed' };
        }
    }
    async handleCancellation() {
        this.status = OrderTicketStatus.cancelled;
        this.$save();
        if (this.order_hash) {
            // Post-completion: order exists, issue fiscal storno
            if (!OrderTicket.canProcess())
                return { success: true };
            const order = this.getOrder();
            if (!order)
                return { success: true };
            // Single-actor storno. The orderTicketCancel broadcast
            // reaches every fiscal terminal and each one used to issue its own
            // storno — N fiscal reversals for one order. Restrict it to the
            // register that issued the order: the fiscally correct actor, since
            // the storno references the original receipt registered on that
            // device. Peers no-op. (Shared registers — same cash_register_id on
            // multiple terminals — still rely on the already-cancelled guard
            // below for the simultaneous case, matching C1's residual race.)
            const terminal = Store.get('terminal');
            if (order.cash_register_id !== terminal?.cash_register_id)
                return { success: true };
            // Idempotency guard: never storno an order that is already cancelled
            // (stale broadcast / snapshot replay).
            if (order.canceled())
                return { success: true };
            try {
                const stornoOrder = await order.prepareCancel();
                await stornoOrder.issue({});
                return { success: true };
            }
            catch (e) {
                console.error('[OrderTicket] Auto-storno failed', e);
                return { success: false, stornoFailed: true };
            }
        }
        else {
            // Pre-completion: no order, just cancel kitchen ticket
            await KitchenTicket.cancelByOrderTicket(this);
            return { success: true };
        }
    }
    async markReady() {
        this.status = OrderTicketStatus.ready;
        // Stamp the prep-end time locally (once) so the orders-hub timer freezes
        // correctly even offline; the backend also sets it on the ready
        // transition. Never overwritten — it must stay pure prep time.
        if (!this.prepared_at) {
            this.prepared_at = moment().utc().toISOString();
        }
        const result = await this.sync();
        if (result.success && this.markReadyFailed) {
            this.markReadyFailed = false;
            this.$save();
        }
        return result;
    }
    async markPickedUp() {
        this.status = OrderTicketStatus.pickedUp;
        return this.sync();
    }
    async confirmPreorder() {
        const previousStatus = this.status;
        this.status = OrderTicketStatus.confirmedPreorder;
        const result = await this.sync();
        if (result.success) {
            // Backend transitions to accepted after confirming with platform
            this.status = OrderTicketStatus.accepted;
            this.$save();
        }
        else {
            // Roll back the optimistic status IN THE STORE too — sync()'s $save
            // already persisted confirmedPreorder, and a memory-only rollback
            // leaves the stored ticket stuck (audit finding M1).
            this.status = previousStatus;
            this.$save();
        }
        return result;
    }
    /**
     * Reject the ticket on the platform + cancel locally.
     *
     * @param payload Optional structured rejection payload. Providers
     *                with capability `structuredRejection` (Glovo/DH)
     *                consume `reasonCode`; providers without it (Wolt)
     *                consume `message` as the consumer-visible string.
     *                The backend forwards both verbatim to the provider.
     */
    async reject(payload) {
        // Locally-created ticket that hasn't synced yet (manual entry while
        // offline, or a queued retry): there is no backend record to reject.
        // Cancel locally — the SyncData queue can drop the pending CREATE.
        if (!this.id) {
            await this.removeFromSync();
            const cancellation = await this.handleCancellation();
            this.broadcastCancellation();
            // Never synced ⇒ the platform has no record to notify.
            return { success: true, stornoFailed: cancellation.stornoFailed, platformNotified: true };
        }
        if (!Store.get('isOnline')) {
            return { success: false };
        }
        let platformNotified = true;
        try {
            const response = await apiService.post('/order-tickets/reject', {
                order_ticket_id: this.id,
                reason_code: payload?.reasonCode ?? null,
                message: payload?.message ?? '',
            });
            // Absent key = older backend that always notified; only an explicit
            // false means the platform turned the rejection down.
            platformNotified = response.data?.platform_notified !== false;
        }
        catch {
            return { success: false };
        }
        const cancellation = await this.handleCancellation();
        this.broadcastCancellation();
        return { success: true, stornoFailed: cancellation.stornoFailed, platformNotified };
    }
    /**
     * Propagate a POS-initiated rejection to peer terminals,
     * frontend-initiated per the rule in EventSystem.md — the backend
     * reject endpoint deliberately does not broadcast. Without this,
     * peers keep the ticket active (fireable, completable) until a
     * snapshot delta delivers the cancelled state.
     */
    broadcastCancellation() {
        OrderTicket.syncTerminals({ items: [{ action: 'patch', record: this.$toJson() }] });
    }
    /**
     * Adjust the platform-side committed pickup time. No-ops on
     * providers without `adjustPreparation` capability (Wolt) — the
     * UI gates the button via hasCapability.
     *
     * @param expectedPickupAt Absolute ISO-8601 datetime.
     */
    async adjustPreparation(expectedPickupAt) {
        if (!this.id || !Store.get('isOnline')) {
            return { success: false };
        }
        try {
            const response = await apiService.post('/order-tickets/adjust-preparation', {
                order_ticket_id: this.id,
                expected_pickup_at: expectedPickupAt,
            });
            if (response.data.ticket) {
                this.$fill(response.data.ticket);
                OrderTicket.insert({ data: [response.data.ticket] });
            }
            return { success: response.data.platform_notified !== false };
        }
        catch {
            return { success: false };
        }
    }
    // ── Product mapping ──
    async mapItem(externalId, productId) {
        if (!Store.get('isOnline')) {
            return { success: false };
        }
        try {
            const response = await apiService.post('/order-tickets/map-product', {
                order_ticket_id: this.id,
                source: this.source,
                external_product_id: externalId,
                product_id: productId,
            });
            if (response.data.ticket) {
                this.$fill(response.data.ticket);
                OrderTicket.insert({ data: [response.data.ticket] });
            }
            if (!this.hasUnmappedItems()) {
                const settings = OrderTicket.getSettings();
                if (!settings?.require_staff_confirmation) {
                    await this.fireToKitchen();
                }
                return { success: true };
            }
            return { success: true };
        }
        catch {
            return { success: false };
        }
    }
    // ── Convenience ──
    getOrder() {
        if (!this.order_hash)
            return undefined;
        return Order.all().find(o => o.hash === this.order_hash);
    }
    /**
     * Build a kitchen slip printable from order ticket data.
     * Used during the acceptance phase before an Order exists.
     */
    async getSlipPrintable(printingSector) {
        const pr = new Printable();
        pr.l();
        // Same opening as Order.getUnpaidOrderPrintable — the callout in the
        // sector banner, the platform named beneath — so the kitchen reads one
        // format whether the slip fires from the hub or from an issued order.
        if (printingSector) {
            const sectorLabel = I18n.t('products.printing_sector.' + printingSector);
            pr.l('L', this.callout_code ? `${this.callout_code} # ${sectorLabel}` : sectorLabel, { align: 'C', scale: '2,2' });
            pr.l();
        }
        pr.l('D', '', { delimiter: '-' });
        // The platform's own reference follows its name (API tickets only):
        // until the packing slip prints at pickup, this slip is the only paper
        // on the bag, and the courier's app shows the platform code, not our
        // callout. No '#' — that prefix means the callout on every other document.
        const platformLine = [OrderTicket.formatSourceLabel(this.source), this.external_code].filter(Boolean).join(' ');
        pr.l('L', platformLine, { scale: '2,2' });
        const { formatDateTimeHuman } = dateFormatter();
        pr.l('L', `${I18n.t('general.time')}: ${formatDateTimeHuman(moment())}`);
        // Same rule as the KDS card (KitchenTicket.createFromOrderTicket): a
        // manual ticket was keyed in by the current user; an API ticket by nobody.
        if (this.isManual()) {
            pr.l('B', `${I18n.t('general.issued_by')}:`, User.current()?.name || '');
        }
        pr.l('D', '', { delimiter: '-' });
        let items = this.items.filter(i => i.isMapped());
        if (printingSector && printingSector !== 'order') {
            items = items.filter(item => {
                const product = item.getMappedProduct();
                return !product || product.printing_sector === printingSector;
            });
        }
        // Body spacing mirrors Order.getUnpaidOrderPrintable line for line.
        pr.l();
        for (const item of items) {
            const product = item.getMappedProduct();
            const name = product?.name || item.name;
            pr.l('L', `${item.quantity} x ${name}`, { scale: '2,2' });
            if (item.attributes.length) {
                pr.l();
            }
            for (const attr of item.attributes) {
                pr.l('L', `- ${attr}`);
            }
            pr.l();
        }
        if (this.special_requirements) {
            pr.l('D', '', { delimiter: '-' });
            pr.l('L', this.special_requirements);
        }
        return pr;
    }
    /**
     * Print preparation slips to all configured sector printers (kitchen, bar, etc.),
     * routed by each product's printing_sector. Mirrors Order.printSlips() but builds
     * printables from OrderTicket data.
     */
    printSlips() {
        const items = {};
        Product.PRINTING_SECTORS.forEach(sector => { items[sector] = false; });
        for (const orderItem of this.items) {
            const product = orderItem.getMappedProduct();
            if (product?.printing_sector && items[product.printing_sector] !== undefined) {
                items[product.printing_sector] = true;
            }
        }
        // Print to "order" role printer (full ticket)
        const orderPrinter = printService.getAssignedPrinter(PrintRoleType.order);
        if (orderPrinter) {
            this.getSlipPrintable('order').then(printable => {
                printable.print({ printer: orderPrinter });
            });
        }
        // Print to sector-specific printers
        let printerIndex = 1;
        Product.PRINTING_SECTORS.forEach(sector => {
            const assignedPrinter = printService.getAssignedPrinter(sector);
            if (items[sector] && assignedPrinter) {
                const delay = printerIndex * 2000;
                setTimeout(() => {
                    this.getSlipPrintable(sector).then(printable => {
                        printable.print({ printer: assignedPrinter });
                    });
                }, delay);
                printerIndex++;
            }
        });
    }
    /**
     * Display label for a source string. Falls back to the raw source
     * when the registry has no entry for it.
     */
    static formatSourceLabel(source) {
        return getProviderLabel(source);
    }
}
export default OrderTicket;
modelRegistry.register('OrderTicket', OrderTicket);
//# sourceMappingURL=OrderTicket.js.map